import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { ReportPreview } from '@/components/patientReports/ReportPreview';
import { ReportSectionCards } from '@/components/patientReports/ReportSectionCards';
import { WordTemplateFileField } from '@/components/patientReports/WordTemplateFileField';
import { WordReportPreview } from '@/components/patientReports/WordReportPreview';
import { usePatientReportTextLibrary } from '@/hooks/usePatientReportTexts';
import { useAuth } from '@/lib/auth';
import { fetchFormsTemplateDetail, useFormsTemplateList } from '@/hooks/useFormsTemplates';
import {
  searchPreviewPatients,
  useDeletePatientReportTemplate,
  usePatientReportSettings,
  usePatientReportSources,
  usePatientReportTemplates,
  useSavePatientReportTemplate,
  useSavePatientReportTemplateCover,
  useSavePatientReportTemplateDocx,
} from '@/hooks/usePatientReports';
import { revealReportCpr } from '@/lib/patientReports/cpr';
import { loadReportAuthorName, wordTemplateFileName } from '@/lib/patientReports/docxFile';
import { EMPTY_REPORT_CATALOG } from '@/lib/patientReports/model';
import { reportPlaceholderContext } from '@/lib/patientReports/placeholders';
import {
  EXAMPLE_CPR,
  EXAMPLE_QUESTIONNAIRE_DETAIL,
  EXAMPLE_REPORT_CATALOG,
  createReportSection,
  previewToPdfBlocks,
  questionsFromUnknown,
  questionnaireSectionFigures,
  missingQuestionnaireEntries,
  resolveLetterheadMode,
  templateSavePayload,
  type PatientReportTemplate,
  type ReportPreviewLabels,
  type ReportSection,
} from '@/lib/patientReports/template';
import type { PatientReportLetterheadChoice } from '@/lib/patientReports/model';
import {
  personalTextBodies,
  personalTextById,
  personalTextManyTypeIds,
  personalTextNeedsCpr,
  resolvePreviewWithPersonalTexts,
} from '@/lib/patientReports/userTexts';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';
import { SectionCard } from '@/pages/settings/settingsChrome';

type Draft = {
  id: string | null;
  name: string;
  reportTitle: string;
  reportTitleFollowsName: boolean;
  description: string;
  includeCover: boolean;
  coverTitle: string;
  coverBody: string;
  letterhead: PatientReportLetterheadChoice;
  sections: ReportSection[];
  docxStoragePath: string | null;
  coverStoragePath: string | null;
  coverMime: 'application/pdf' | 'image/png' | 'image/jpeg' | null;
};

function emptyDraft(labels: { patient: string; markings: string; text: string }): Draft {
  return {
    id: null,
    name: '',
    reportTitle: '',
    reportTitleFollowsName: true,
    description: '',
    includeCover: false,
    coverTitle: '',
    coverBody: '',
    letterhead: 'inherit',
    sections: [
      createReportSection('patient_fields', labels.patient),
      createReportSection('markings', labels.markings),
      createReportSection('text', labels.text),
    ],
    docxStoragePath: null,
    coverStoragePath: null,
    coverMime: null,
  };
}

function draftFromTemplate(template: PatientReportTemplate): Draft {
  const custom = template.reportTitle?.trim() ?? '';
  const follows = !custom || custom === template.name;
  return {
    id: template.id,
    name: template.name,
    reportTitle: follows ? template.name : custom,
    reportTitleFollowsName: follows,
    description: template.description,
    includeCover: template.includeCover,
    coverTitle: template.coverTitle,
    coverBody: template.coverBody,
    letterhead: template.letterhead,
    sections: template.sections,
    docxStoragePath: template.docxStoragePath ?? null,
    coverStoragePath: template.coverStoragePath ?? null,
    coverMime: template.coverMime ?? null,
  };
}

export function PatientReportTemplateSettings({
  clinicId,
  canEdit,
  clinicLetterhead,
}: {
  clinicId: string | null;
  canEdit: boolean;
  clinicLetterhead: 'none' | 'invoice' | 'custom';
}) {
  const { t } = useTranslation();
  const list = usePatientReportTemplates(clinicId);
  const save = useSavePatientReportTemplate(clinicId);
  const uploadDocx = useSavePatientReportTemplateDocx(clinicId);
  const uploadCover = useSavePatientReportTemplateCover(clinicId);
  const remove = useDeletePatientReportTemplate(clinicId);
  const settings = usePatientReportSettings(clinicId);
  const [draft, setDraft] = useState<Draft | null>(null);

  if (list.isLoading) {
    return <p className={cn(TYPE.body, 'text-ds-muted')}>{t('patientReports.loading')}</p>;
  }
  if (list.error) {
    return <p className={cn(TYPE.body, 'text-ds-red-ink')}>{t('patientReports.load_error')}</p>;
  }

  return (
    <SectionCard
      title={t('patientReports.templates_title')}
      subtitle={t('patientReports.templates_hint')}
    >
      <p className={cn(TYPE.small, 'mb-s4 text-ds-muted')}>{t('patientReports.templates_v1')}</p>
      {draft ? (
        <TemplateDraftEditor
          clinicId={clinicId}
          canEdit={canEdit}
          draft={draft}
          tags={list.data?.tags ?? []}
          clinicLetterhead={clinicLetterhead}
          saving={save.isPending}
          docxBusy={uploadDocx.isPending}
          coverBusy={uploadCover.isPending}
          clinicWordPath={settings.data?.docxTemplateStoragePath ?? null}
          onChange={setDraft}
          onUploadDocx={async (file) => {
            if (!draft.id) return;
            try {
              const path = await uploadDocx.mutateAsync({ templateId: draft.id, file });
              setDraft((current) => (current ? { ...current, docxStoragePath: path } : current));
              toast.success(t('settings.integrations.patient_reports.word_saved_toast'));
            } catch (err) {
              const message = err instanceof Error ? err.message : '';
              toast.error(
                message === 'docx-cpr'
                  ? t('settings.integrations.patient_reports.word_cpr')
                  : message === 'docx-type'
                    ? t('settings.integrations.patient_reports.word_type')
                    : t('settings.integrations.patient_reports.word_save_error'),
              );
              throw err;
            }
          }}
          onClearDocx={async () => {
            if (!draft.id) return;
            try {
              await uploadDocx.mutateAsync({ templateId: draft.id, clear: true });
              setDraft((current) => (current ? { ...current, docxStoragePath: null } : current));
              toast.success(t('settings.integrations.patient_reports.word_removed_toast'));
            } catch (err) {
              toast.error(t('settings.integrations.patient_reports.word_save_error'));
              throw err;
            }
          }}
          onUploadCover={async (file) => {
            if (!draft.id) return;
            try {
              const saved = await uploadCover.mutateAsync({ templateId: draft.id, file });
              setDraft((current) =>
                current
                  ? {
                      ...current,
                      coverStoragePath: saved.path,
                      coverMime:
                        saved.mime === 'application/pdf' ||
                        saved.mime === 'image/png' ||
                        saved.mime === 'image/jpeg'
                          ? saved.mime
                          : null,
                    }
                  : current,
              );
              toast.success(t('patientReports.cover_file_saved_toast'));
            } catch (err) {
              const message = err instanceof Error ? err.message : '';
              toast.error(
                message === 'cover-type'
                  ? t('patientReports.cover_file_type')
                  : t('patientReports.cover_file_error'),
              );
            }
          }}
          onClearCover={async () => {
            if (!draft.id) return;
            try {
              await uploadCover.mutateAsync({ templateId: draft.id, clear: true });
              setDraft((current) =>
                current ? { ...current, coverStoragePath: null, coverMime: null } : current,
              );
              toast.success(t('patientReports.cover_file_removed_toast'));
            } catch {
              toast.error(t('patientReports.cover_file_error'));
            }
          }}
          onClose={() => setDraft(null)}
          onSave={async () => {
            if (!draft.name.trim()) {
              toast.error(t('patientReports.templates_name_required'));
              return;
            }
            const payload = templateSavePayload(draft);
            try {
              const saved = await save.mutateAsync({ templateId: draft.id, ...payload });
              setDraft(draftFromTemplate(saved));
              toast.success(t('patientReports.templates_saved'));
            } catch {
              toast.error(t('patientReports.templates_save_error'));
            }
          }}
        />
      ) : (
        <div className="flex flex-col gap-s3">
          {canEdit ? (
            <div>
              <Button
                onClick={() =>
                  setDraft(
                    emptyDraft({
                      patient: t('patientReports.add_patient'),
                      markings: t('patientReports.add_markings'),
                      text: t('patientReports.text_heading'),
                    }),
                  )
                }
              >
                {t('patientReports.templates_new')}
              </Button>
            </div>
          ) : null}
          {(list.data?.templates.length ?? 0) === 0 ? (
            <p className={cn(TYPE.body, 'text-ds-muted')}>{t('patientReports.templates_empty')}</p>
          ) : (
            <ul className="divide-y divide-ds-line rounded-ctl border border-ds-line">
              {list.data?.templates.map((template) => (
                <li key={template.id} className="flex flex-wrap items-center gap-s3 px-s4 py-s3">
                  <div className="min-w-0 flex-1">
                    <p className={cn(TYPE.body, 'font-medium text-ds-text')}>{template.name}</p>
                    {template.description ? (
                      <p className={cn(TYPE.small, 'text-ds-muted')}>{template.description}</p>
                    ) : null}
                    {template.docxStoragePath ? (
                      <p className={cn(TYPE.small, 'text-ds-muted')}>
                        {t('patientReports.templates_word_saved')}
                      </p>
                    ) : null}
                    {template.coverStoragePath ? (
                      <p className={cn(TYPE.small, 'text-ds-muted')}>
                        {t('patientReports.cover_file_saved')}
                      </p>
                    ) : null}
                  </div>
                  <Button variant="secondary" onClick={() => setDraft(draftFromTemplate(template))}>
                    {t('patientReports.templates_edit')}
                  </Button>
                  {canEdit ? (
                    <Button
                      variant="tertiary"
                      loading={remove.isPending}
                      onClick={() => {
                        if (!window.confirm(t('patientReports.templates_delete_confirm'))) return;
                        void remove.mutateAsync(template.id).catch(() => {
                          toast.error(t('patientReports.templates_save_error'));
                        });
                      }}
                    >
                      {t('patientReports.templates_delete')}
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </SectionCard>
  );
}

function TemplateDraftEditor({
  clinicId,
  canEdit,
  draft,
  tags,
  clinicLetterhead,
  saving,
  docxBusy,
  coverBusy,
  clinicWordPath,
  onChange,
  onClose,
  onSave,
  onUploadDocx,
  onClearDocx,
  onUploadCover,
  onClearCover,
}: {
  clinicId: string | null;
  canEdit: boolean;
  draft: Draft;
  tags: readonly { id: string; name: string }[];
  clinicLetterhead: 'none' | 'invoice' | 'custom';
  saving: boolean;
  docxBusy: boolean;
  coverBusy: boolean;
  clinicWordPath: string | null;
  onChange: (draft: Draft) => void;
  onClose: () => void;
  onSave: () => void;
  onUploadDocx: (file: File) => Promise<void>;
  onClearDocx: () => Promise<void>;
  onUploadCover: (file: File) => Promise<void>;
  onClearCover: () => Promise<void>;
}) {
  const { t, i18n } = useTranslation();
  const { hasPermission } = useAuth();
  const library = usePatientReportTextLibrary(clinicId);
  const personalBodies = useMemo(() => personalTextBodies(library.texts), [library.texts]);
  const personalById = useMemo(() => personalTextById(library.texts), [library.texts]);
  const manyTextTypes = useMemo(() => personalTextManyTypeIds(library.texts), [library.texts]);
  const [authorName, setAuthorName] = useState('');
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [wordRevision, setWordRevision] = useState(0);
  const forms = useFormsTemplateList(clinicId, i18n.language);
  const [previewMode, setPreviewMode] = useState<'example' | 'patient'>('example');
  const [patientQuery, setPatientQuery] = useState('');
  const [hits, setHits] = useState<{ id: string; name: string }[]>([]);
  const [pickedId, setPickedId] = useState<string | null>(null);
  const sources = usePatientReportSources(
    clinicId,
    pickedId,
    previewMode === 'patient' && Boolean(pickedId),
  );
  const [cpr, setCpr] = useState<string | null>(null);
  const [cprAccess, setCprAccess] = useState<'hidden' | 'loading' | 'ready'>('hidden');
  const [formQuestions, setFormQuestions] = useState<
    Record<string, { id: string; text: string }[]>
  >({});

  const formOptions = useMemo(() => {
    const rows = [...(forms.data?.clinic ?? []), ...(forms.data?.platform ?? [])];
    return rows.map((row) => ({
      id: row.id,
      name: row.display_name_da || row.display_name_en || row.id,
    }));
  }, [forms.data]);

  const templateIds = draft.sections
    .flatMap((section) =>
      section.kind === 'questionnaire' && section.formsTemplateId ? [section.formsTemplateId] : [],
    )
    .join('|');

  useEffect(() => {
    if (!clinicId || !templateIds) return;
    let cancelled = false;
    void Promise.all(
      templateIds.split('|').map(async (templateId) => {
        try {
          const detail = await fetchFormsTemplateDetail(clinicId, templateId);
          return [templateId, questionsFromUnknown(detail.json_schema_preview)] as const;
        } catch {
          return [templateId, []] as const;
        }
      }),
    ).then((rows) => {
      if (cancelled) return;
      const next: Record<string, { id: string; text: string }[]> = {};
      for (const section of draft.sections) {
        if (section.kind !== 'questionnaire' || !section.formsTemplateId) continue;
        next[section.id] = [...(rows.find((row) => row[0] === section.formsTemplateId)?.[1] ?? [])];
      }
      setFormQuestions(next);
    });
    return () => {
      cancelled = true;
    };
  }, [clinicId, draft.sections, templateIds]);

  useEffect(() => {
    if (previewMode !== 'patient' || !clinicId) return;
    const handle = window.setTimeout(() => {
      void searchPreviewPatients(clinicId, patientQuery)
        .then(setHits)
        .catch(() => setHits([]));
    }, 250);
    return () => window.clearTimeout(handle);
  }, [clinicId, patientQuery, previewMode]);

  const needsCpr =
    draft.sections.some(
      (section) => section.kind === 'patient_fields' && section.fields.includes('cpr'),
    ) || personalTextNeedsCpr(draft.sections, personalBodies, personalById);

  useEffect(() => {
    let cancelled = false;
    void loadReportAuthorName()
      .then((name) => {
        if (!cancelled && name) setAuthorName(name);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);
  const canReveal = Boolean(clinicId && hasPermission(clinicId, 'patients_cpr', 'view'));

  useEffect(() => {
    if (previewMode !== 'patient' || !pickedId || !needsCpr) {
      setCpr(null);
      setCprAccess(needsCpr && previewMode === 'patient' && !canReveal ? 'hidden' : 'ready');
      return;
    }
    if (!canReveal) {
      setCpr(null);
      setCprAccess('hidden');
      return;
    }
    let cancelled = false;
    setCprAccess('loading');
    void revealReportCpr(pickedId)
      .then((value) => {
        if (cancelled) return;
        setCpr(value);
        setCprAccess('ready');
      })
      .catch(() => {
        if (cancelled) return;
        setCpr(null);
        setCprAccess('ready');
      });
    return () => {
      cancelled = true;
      setCpr(null);
    };
  }, [canReveal, needsCpr, pickedId, previewMode]);

  const questions = { ...formQuestions };
  if (previewMode === 'example') {
    for (const section of draft.sections) {
      if (section.kind === 'questionnaire' && (questions[section.id]?.length ?? 0) === 0) {
        questions[section.id] = EXAMPLE_QUESTIONNAIRE_DETAIL.answers.map((answer) => ({
          id: answer.id,
          text: answer.prompt,
        }));
      }
    }
  }

  const catalog =
    previewMode === 'example' ? EXAMPLE_REPORT_CATALOG : (sources.data ?? EMPTY_REPORT_CATALOG);
  const chartChoices: Record<string, { id: string; label: string }[]> = {};
  for (const section of draft.sections) {
    if (section.kind !== 'questionnaire') continue;
    const detail =
      previewMode === 'example'
        ? EXAMPLE_QUESTIONNAIRE_DETAIL
        : null;
    chartChoices[section.id] = questionnaireSectionFigures({
      section,
      detail,
      catalog,
      language: i18n.language,
    })
      .filter((chart) => chart.id)
      .map((chart) => ({ id: chart.id ?? '', label: chart.label }));
  }
  const labels = previewLabels(t);
  const model = resolvePreviewWithPersonalTexts(
    {
      title: draft.name || t('patientReports.templates_name'),
      includeCover: draft.includeCover,
      coverTitle: draft.coverTitle,
      coverBody: draft.coverBody,
      letterhead: draft.letterhead,
      clinicLetterhead,
      sections: draft.sections,
      catalog,
      cprAccess: previewMode === 'example' ? 'ready' : cprAccess,
      cpr: previewMode === 'example' ? EXAMPLE_CPR : cpr,
      details:
        previewMode === 'example' ? { 'example-assignment': EXAMPLE_QUESTIONNAIRE_DETAIL } : {},
      fallbackDetail: previewMode === 'example' ? EXAMPLE_QUESTIONNAIRE_DETAIL : null,
      labels,
      showEmpty: true,
      language: i18n.language,
    },
    {
      bodies: personalBodies,
      byId: personalById,
      manyTypeIds: manyTextTypes,
      authorName,
      clinicName: '',
      generatedAt: new Date(),
      missingText: t('patientReports.user_text_missing'),
      chooseText: t('patientReports.user_text_choose'),
      pickText: t('patientReports.user_text_pick'),
    },
  );
  const wordPath = draft.docxStoragePath || clinicWordPath;

  return (
    <div className="flex flex-col gap-s4">
      <div className="flex flex-wrap gap-s2">
        <Button variant="tertiary" onClick={onClose}>
          {t('patientReports.templates_back')}
        </Button>
        {canEdit ? (
          <Button loading={saving} onClick={onSave}>
            {t('patientReports.templates_save')}
          </Button>
        ) : null}
      </div>
      <div className="grid items-start gap-s4 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div className="flex flex-col gap-s3">
          <Input
            label={t('patientReports.templates_name')}
            value={draft.name}
            disabled={!canEdit}
            onChange={(event) => {
              const name = event.target.value;
              onChange({
                ...draft,
                name,
                reportTitle: draft.reportTitleFollowsName ? name : draft.reportTitle,
              });
            }}
          />
          <Input
            label={t('patientReports.templates_report_name')}
            hint={t('patientReports.templates_report_name_hint')}
            value={draft.reportTitleFollowsName ? draft.name : draft.reportTitle}
            disabled={!canEdit}
            onChange={(event) => {
              const reportTitle = event.target.value;
              onChange({
                ...draft,
                reportTitle,
                reportTitleFollowsName: reportTitle.trim() === draft.name.trim(),
              });
            }}
          />
          <Textarea
            label={t('patientReports.templates_description')}
            value={draft.description}
            disabled={!canEdit}
            onChange={(event) => onChange({ ...draft, description: event.target.value })}
          />
          <div className="rounded-ctl border border-ds-line px-s4 py-s3">
            <p className={cn(TYPE.body, 'font-medium text-ds-text')}>
              {t('patientReports.templates_word')}
            </p>
            <p className={cn(TYPE.small, 'mt-s1 text-ds-muted')}>
              {draft.id
                ? t('patientReports.templates_word_hint')
                : t('patientReports.templates_word_need_save')}
            </p>
            {draft.id ? (
              <WordTemplateFileField
                className="mt-s3"
                inputId="patient-report-template-word"
                storagePath={draft.docxStoragePath}
                downloadName={wordTemplateFileName(
                  draft.name.trim() ? `${draft.name.trim()} Word-skabelon` : 'Word-skabelon',
                )}
                canEdit={canEdit}
                busy={docxBusy}
                onReplace={async (file) => {
                  await onUploadDocx(file);
                  setWordRevision((revision) => revision + 1);
                }}
                onRemove={async () => {
                  await onClearDocx();
                  setWordRevision((revision) => revision + 1);
                }}
              />
            ) : null}
          </div>
          <Select
            label={t('patientReports.letterhead_label')}
            value={draft.letterhead}
            disabled={!canEdit}
            onChange={(event) =>
              onChange({
                ...draft,
                letterhead: event.target.value as PatientReportLetterheadChoice,
              })
            }
          >
            <option value="inherit">{t('patientReports.letterhead_inherit')}</option>
            <option value="none">
              {t('settings.integrations.patient_reports.letterhead_none')}
            </option>
            <option value="invoice">
              {t('settings.integrations.patient_reports.letterhead_invoice')}
            </option>
            <option value="custom">
              {t('settings.integrations.patient_reports.letterhead_custom')}
            </option>
          </Select>
          <label className="flex items-center gap-s2">
            <input
              type="checkbox"
              checked={draft.includeCover}
              disabled={!canEdit}
              onChange={(event) => onChange({ ...draft, includeCover: event.target.checked })}
            />
            <span>{t('patientReports.cover')}</span>
          </label>
          <p className={cn(TYPE.small, 'text-ds-muted')}>{t('patientReports.cover_hint')}</p>
          {draft.includeCover ? (
            <div className="rounded-ctl border border-ds-line px-s4 py-s3">
              <p className={cn(TYPE.body, 'font-medium text-ds-text')}>
                {t('patientReports.cover_file_title')}
              </p>
              <p className={cn(TYPE.small, 'mt-s1 text-ds-muted')}>
                {draft.id
                  ? t('patientReports.cover_file_hint')
                  : t('patientReports.cover_file_need_save')}
              </p>
              {draft.coverStoragePath ? (
                <p className={cn(TYPE.small, 'mt-s2 text-ds-muted')}>
                  {t('patientReports.cover_file_saved')}
                </p>
              ) : null}
              {canEdit && draft.id ? (
                <div className="mt-s3 flex flex-col gap-s3">
                  <input
                    type="file"
                    accept="application/pdf,image/png,image/jpeg,.pdf,.png,.jpg,.jpeg"
                    disabled={coverBusy}
                    className="block w-full text-sm text-ds-text"
                    onChange={(event) => setCoverFile(event.target.files?.[0] ?? null)}
                  />
                  <div className="flex flex-wrap gap-s2">
                    <Button
                      type="button"
                      variant="secondary"
                      loading={coverBusy}
                      onClick={() => {
                        if (!coverFile) {
                          toast.error(t('patientReports.cover_file_required'));
                          return;
                        }
                        void onUploadCover(coverFile).then(() => setCoverFile(null));
                      }}
                    >
                      {t('patientReports.cover_file_upload')}
                    </Button>
                    {draft.coverStoragePath ? (
                      <Button
                        type="button"
                        variant="tertiary"
                        loading={coverBusy}
                        onClick={() => void onClearCover()}
                      >
                        {t('patientReports.cover_file_remove')}
                      </Button>
                    ) : null}
                  </div>
                </div>
              ) : null}
              <div className="mt-s4 flex flex-col gap-s3">
                <p className={cn(TYPE.small, 'text-ds-muted')}>
                  {t('patientReports.cover_file_fallback')}
                </p>
                <Input
                  label={t('patientReports.cover_title')}
                  value={draft.coverTitle}
                  disabled={!canEdit}
                  onChange={(event) => onChange({ ...draft, coverTitle: event.target.value })}
                />
                <Textarea
                  label={t('patientReports.cover_body')}
                  value={draft.coverBody}
                  disabled={!canEdit}
                  onChange={(event) => onChange({ ...draft, coverBody: event.target.value })}
                />
              </div>
            </div>
          ) : draft.coverStoragePath ? (
            <p className={cn(TYPE.small, 'text-ds-muted')}>{t('patientReports.cover_file_off')}</p>
          ) : null}
          <ReportSectionCards
            sections={draft.sections}
            tags={tags}
            questions={questions}
            charts={chartChoices}
            formOptions={formOptions}
            patientQuestionnaires={previewMode === 'patient' ? catalog.questionnaires : []}
            files={previewMode === 'patient' ? catalog.files : undefined}
            missingFormIds={
              previewMode === 'patient'
                ? missingQuestionnaireEntries(draft.sections, catalog).flatMap((form) => [
                    form.id,
                    form.name,
                  ])
                : []
            }
            textTypes={library.types}
            disabled={!canEdit}
            onChange={(sections) => onChange({ ...draft, sections })}
          />
        </div>
        <div>
          <div className="mb-s3 flex flex-wrap gap-s2">
            <Button
              variant={previewMode === 'example' ? 'primary' : 'secondary'}
              onClick={() => setPreviewMode('example')}
            >
              {t('patientReports.preview_example')}
            </Button>
            <Button
              variant={previewMode === 'patient' ? 'primary' : 'secondary'}
              onClick={() => setPreviewMode('patient')}
            >
              {t('patientReports.preview_patient')}
            </Button>
          </div>
          {previewMode === 'patient' ? (
            <div className="mb-s3">
              <Input
                label={t('patientReports.preview_search')}
                value={patientQuery}
                onChange={(event) => setPatientQuery(event.target.value)}
              />
              <ul className="mt-s2">
                {hits.map((hit) => (
                  <li key={hit.id}>
                    <button
                      type="button"
                      className={cn(TYPE.small, 'text-ds-text')}
                      onClick={() => setPickedId(hit.id)}
                    >
                      {hit.name}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          <label className="mb-s3 flex items-start gap-s2">
            <input
              type="checkbox"
              className="mt-1"
              checked={draft.includeCover}
              disabled={!canEdit}
              onChange={(event) => onChange({ ...draft, includeCover: event.target.checked })}
            />
            <span>
              <span className={cn(TYPE.body, 'text-ds-text')}>
                {t('patientReports.preview_cover')}
              </span>
              <span className={cn(TYPE.small, 'mt-s1 block text-ds-muted')}>
                {t('patientReports.preview_cover_hint')}
              </span>
            </span>
          </label>
          {wordPath ? (
            <WordReportPreview
              clinicId={clinicId}
              wordPath={wordPath}
              coverPath={draft.includeCover ? draft.coverStoragePath : null}
              coverMime={draft.coverMime}
              includeCover={draft.includeCover}
              letterhead={draft.letterhead}
              context={reportPlaceholderContext({
                reportTitle: model.title,
                authorName,
                clinicName: '',
                generatedAt: new Date(),
                patient: catalog.patient,
                cprAccess: previewMode === 'example' ? 'ready' : cprAccess,
                cpr: previewMode === 'example' ? null : cpr,
                cprHidden: labels.cprHidden,
                cprLoading: labels.cprLoading,
                coverTitle: draft.coverTitle,
                coverBody: draft.coverBody,
                sections: draft.sections,
                model,
              })}
              blocks={previewToPdfBlocks(model)}
              patientId={previewMode === 'patient' ? pickedId : null}
              canRevealCpr={canReveal}
              sampleCpr={previewMode === 'example' ? EXAMPLE_CPR : null}
              sourceRevision={wordRevision}
              fallback={
                <ReportPreview
                  model={model}
                  letterheadLabel={t(
                    `patientReports.letterhead_${resolveLetterheadMode(draft.letterhead, clinicLetterhead)}`,
                  )}
                />
              }
            />
          ) : (
            <ReportPreview
              model={model}
              letterheadLabel={t(
                `patientReports.letterhead_${resolveLetterheadMode(draft.letterhead, clinicLetterhead)}`,
              )}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function previewLabels(t: (key: string) => string): ReportPreviewLabels {
  return {
    name: t('patientReports.field_name'),
    cpr: t('patientReports.field_cpr'),
    email: t('patientReports.field_email'),
    phone: t('patientReports.field_phone'),
    address: t('patientReports.field_address'),
    jobTitle: t('patientReports.field_job'),
    cave: t('patientReports.field_cave'),
    notes: t('patientReports.field_notes'),
    diagnosis: t('patientReports.field_diagnosis'),
    patNumber: t('patientReports.field_pat_number'),
    locale: t('patientReports.field_locale'),
    notRecorded: t('patientReports.not_recorded'),
    cprHidden: t('patientReports.cpr_hidden'),
    cprLoading: t('patientReports.cpr_loading'),
    comment: t('patientReports.field_comment'),
    file: t('patientReports.kind_file'),
    page: t('patientReports.page'),
    quote: t('patientReports.field_quote'),
    score: t('patientReports.field_score'),
    answer: t('patientReports.field_answer'),
    missingQuestionnaire: t('patientReports.missing_questionnaire'),
    chooseQuestionnaire: t('patientReports.form_none'),
    chooseJournal: t('patientReports.journal_pick'),
    chooseMarking: t('patientReports.marking_pick'),
    formMissing: t('patientReports.form_missing'),
  };
}
